/**
 * Cases the courts have gone quiet on.
 *
 * Lloyd, Aug 19: *"I think the general rule should be 45 days of silence, the court gets an email
 * asking for a status update."* This is the review screen that comes before any of that is sent.
 *
 * ## It shows the work; it does not do it
 *
 * There is no send button, because `stale_case.court_autosend` is off and the route behind this
 * screen cannot send anything. Staff read the list and act on it themselves. The button arrives
 * when the firm has looked at a batch and agreed it is right.
 *
 * ## Three numbers, kept apart
 *
 * **Waiting** is how many cases match. **Shown** is how many this batch covers — the cap is a
 * review limit, and the oldest bucket runs past a thousand cases, at which point "a person reviews
 * the batch" stops meaning anything. **No court email** is different again: those cases are not
 * waiting on us at all, they cannot be chased until somebody fills in the court record, and they
 * are the oldest cases in the firm. Folding them into one total would bury a data problem inside a
 * workload number.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

interface StaleCaseRow {
  ticketGuid: string;
  ticketNumber: string | null;
  ageDays: number;
  daysSilent: number;
  courtName: string | null;
  courtEmail: string | null;
  variant: 'status-request' | 'closing-paperwork' | 'setting-request';
  hasUpcomingSetting: boolean;
  explanation: string;
}

/** What each wording actually asks the clerk for, so a reviewer can group a batch by eye. */
const VARIANT_LABELS: Record<StaleCaseRow['variant'], string> = {
  'closing-paperwork': 'Asks for closing paperwork',
  'setting-request': 'Asks whether it has been set',
  'status-request': 'Asks for a status update',
};

interface StaleCaseResponse {
  stageMonths: number;
  stages: number[];
  matchedTotal: number;
  droppedToCap: number;
  withoutCourtEmail: number;
  summary: string;
  cases: StaleCaseRow[];
}

declare const process: { env: { API_HOST?: string } };

function ManagerStaleCasesView() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState<StaleCaseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const host = process.env.API_HOST?.trim() || window.location.origin;
      const response = await fetch(
        `${host}/api/v1/manager/stale-cases?months=${months}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load the stale-case list');
      }
      setData(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the stale-case list');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Cases waiting on a court
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Open cases where the court has not been in touch for 45 days. Nothing here has been sent —
        this is the list to work through, oldest silence first.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={months}
        onChange={(_, value) => setMonths(value)}
        sx={{ mb: 2 }}
      >
        {(data?.stages ?? [6, 5, 4, 3]).map((stage) => (
          <Tab key={stage} value={stage} label={`${stage}+ months`} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      ) : !data ? null : (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${data.matchedTotal} waiting`} />
            <Chip label={`${data.cases.length} shown`} variant="outlined" />
            {/* Named rather than hidden: a batch that quietly covered a third of what it claimed
                would be worse than one that says so. */}
            {data.droppedToCap > 0 && (
              <Chip
                color="warning"
                variant="outlined"
                label={`${data.droppedToCap} not covered by this batch`}
              />
            )}
            {data.withoutCourtEmail > 0 && (
              <Chip
                color="error"
                variant="outlined"
                label={`${data.withoutCourtEmail} have no court email`}
              />
            )}
          </Stack>

          {data.withoutCourtEmail > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {data.withoutCourtEmail} of these cases cannot be chased at all, because their court
              has no usable email address on file. They are among the oldest cases we hold. The
              court record needs filling in before anything else will reach them.
            </Alert>
          )}

          {data.cases.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing is waiting in this bucket.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {data.cases.map((row) => (
                <Paper key={row.ticketGuid} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'medium', minWidth: 140 }}>
                      {row.ticketNumber || row.ticketGuid.slice(0, 8)}
                    </Typography>
                    <Chip size="small" label={`${row.daysSilent}d silent`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={VARIANT_LABELS[row.variant] ?? 'Asks for a status update'}
                    />
                    {!row.hasUpcomingSetting && (
                      // Worth seeing even on the older cases, which get the closing-paperwork
                      // wording: a stale case with nothing on the calendar is a different problem
                      // from one that is merely quiet.
                      <Chip size="small" color="warning" variant="outlined" label="No setting" />
                    )}
                    {!row.courtEmail && (
                      <Chip size="small" color="error" variant="outlined" label="No court email" />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {row.courtName ?? 'No court on the case'} · {row.explanation}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

export default ManagerStaleCasesView;
